<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
/* eslint-disable @intlify/vue-i18n/no-raw-text */

import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const cloneFlows = () => ({
  ...(props.modelValue || {}),
});

const ensureFlow = () => {
  const flows = cloneFlows();

  if (!flows.internet_signup) {
    flows.internet_signup = {
      enabled: true,
      name: 'Contratação de Internet',
      triggers: [],
      destination_sector: 'comercial',
      cities: {},
      messages: {
        ask_city:
          'Para apresentar os planos e valores corretos, em qual cidade você deseja contratar?',
        ask_address:
          'Para verificar a viabilidade, qual é o endereço de instalação?',
        ask_cep: 'Obrigado! Qual é o CEP do endereço de instalação?',
        ask_district: 'Obrigado! Qual é o bairro do endereço de instalação?',
        ask_location:
          'Obrigado! Para concluir a verificação de viabilidade, envie sua localização fixa pelo WhatsApp.',
        location_received:
          'Recebi sua localização. Vou encaminhar seus dados para um atendente verificar a viabilidade da instalação e dar continuidade à contratação.',
      },
    };

    emit('update:modelValue', flows);
  }

  if (!Array.isArray(flows.internet_signup.triggers)) {
    flows.internet_signup.triggers = [];
  }

  if (
    !flows.internet_signup.cities ||
    typeof flows.internet_signup.cities !== 'object'
  ) {
    flows.internet_signup.cities = {};
  }

  if (
    !flows.internet_signup.messages ||
    typeof flows.internet_signup.messages !== 'object'
  ) {
    flows.internet_signup.messages = {};
  }

  return flows.internet_signup;
};

const flow = computed(() => ensureFlow());

const cityEntries = computed(() => Object.entries(flow.value.cities || {}));

const triggersText = computed({
  get() {
    return (flow.value.triggers || []).join('\n');
  },

  set(value) {
    flow.value.triggers = String(value || '')
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean);
  },
});

const normalizeKey = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const addCity = () => {
  let number = 1;
  let key = `nova_cidade_${number}`;

  while (flow.value.cities[key]) {
    number += 1;
    key = `nova_cidade_${number}`;
  }

  flow.value.cities[key] = {
    name: 'Nova cidade',
    aliases: [],
    plans: [],
  };
};

const removeCity = key => {
  delete flow.value.cities[key];
};

const cityAliasesText = city =>
  Array.isArray(city.aliases) ? city.aliases.join('\n') : '';

const setCityAliases = (city, value) => {
  city.aliases = String(value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);
};

const addPlan = city => {
  if (!Array.isArray(city.plans)) {
    city.plans = [];
  }

  city.plans.push({
    key: '',
    name: '',
    price: '',
    details: '',
    aliases: [],
  });
};

const removePlan = (city, index) => {
  city.plans.splice(index, 1);
};

const planAliasesText = plan =>
  Array.isArray(plan.aliases) ? plan.aliases.join('\n') : '';

const setPlanAliases = (plan, value) => {
  plan.aliases = String(value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);
};

const normalizeBeforeSave = () => {
  const cities = {};
  const currentFlow = flow.value;

  Object.entries(currentFlow.cities || {}).forEach(([oldKey, city]) => {
    if (!city) return;

    let key = normalizeKey(city.name) || normalizeKey(oldKey) || 'cidade';

    const originalKey = key;
    let number = 2;

    while (cities[key]) {
      key = `${originalKey}_${number}`;
      number += 1;
    }

    cities[key] = {
      ...city,
      name: String(city.name || '').trim(),
      aliases: Array.isArray(city.aliases) ? city.aliases : [],
      plans: Array.isArray(city.plans) ? city.plans : [],
    };
  });

  currentFlow.cities = cities;

  return props.modelValue;
};

defineExpose({
  normalizeBeforeSave,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <div class="p-5 mt-5 border rounded-xl border-n-weak bg-n-alpha-1">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 class="text-lg font-semibold text-n-slate-12">
          Fluxos automáticos
        </h3>

        <p class="mt-1 text-sm text-n-slate-11">
          Configure atendimentos estruturados sem consumir IA.
        </p>
      </div>

      <label
        class="flex items-center gap-2 px-3 py-2 border rounded-lg border-n-weak"
      >
        <input v-model="flow.enabled" type="checkbox" />

        <span class="text-sm font-medium text-n-slate-12"> Ativado </span>
      </label>
    </div>

    <div class="grid grid-cols-1 gap-4 mt-5 md:grid-cols-2">
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          Nome do fluxo
        </label>

        <input
          v-model="flow.name"
          type="text"
          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
        />
      </div>

      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          Setor de destino
        </label>

        <select
          v-model="flow.destination_sector"
          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
        >
          <option value="comercial">Comercial</option>
          <option value="financeiro">Financeiro</option>
          <option value="suporte">Suporte</option>
          <option value="cancelamento">Cancelamento</option>
          <option value="geral">Atendimento geral</option>
        </select>
      </div>
    </div>

    <div class="mt-5">
      <label class="block mb-1 text-sm font-medium text-n-slate-12">
        Frases que iniciam o fluxo
      </label>

      <textarea
        v-model="triggersText"
        rows="6"
        placeholder="Uma frase por linha"
        class="w-full px-3 py-3 font-mono text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12"
      />
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 mt-7">
      <div>
        <h4 class="font-semibold text-n-slate-12">Cidades e planos</h4>

        <p class="mt-1 text-xs text-n-slate-10">
          Cada cidade pode possuir preços e planos diferentes.
        </p>
      </div>

      <button
        type="button"
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
        @click="addCity"
      >
        + Adicionar cidade
      </button>
    </div>

    <div
      v-if="!cityEntries.length"
      class="p-5 mt-4 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
    >
      Nenhuma cidade cadastrada.
    </div>

    <div v-else class="mt-4 space-y-5">
      <div
        v-for="[cityKey, city] in cityEntries"
        :key="cityKey"
        class="p-4 border rounded-xl border-n-weak bg-n-background"
      >
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="flex-1">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              Cidade
            </label>

            <input
              v-model="city.name"
              type="text"
              class="w-full max-w-lg px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            />
          </div>

          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg border-n-ruby-7 text-n-ruby-11"
            @click="removeCity(cityKey)"
          >
            Excluir cidade
          </button>
        </div>

        <div class="mt-4">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            Outros nomes da cidade
          </label>

          <textarea
            :value="cityAliasesText(city)"
            rows="2"
            placeholder="Um nome por linha"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            @change="setCityAliases(city, $event.target.value)"
          />
        </div>

        <div class="flex items-center justify-between gap-3 mt-6">
          <h5 class="font-medium text-n-slate-12">Planos</h5>

          <button
            type="button"
            class="px-3 py-2 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12"
            @click="addPlan(city)"
          >
            + Adicionar plano
          </button>
        </div>

        <div
          v-if="!city.plans?.length"
          class="p-4 mt-3 text-sm text-center border border-dashed rounded-lg border-n-weak text-n-slate-10"
        >
          Nenhum plano cadastrado.
        </div>

        <div v-else class="mt-3 space-y-4">
          <div
            v-for="(plan, planIndex) in city.plans"
            :key="planIndex"
            class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <label class="block mb-1 text-xs text-n-slate-10">
                  Código
                </label>

                <input
                  v-model="plan.key"
                  type="text"
                  placeholder="600"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                />
              </div>

              <div>
                <label class="block mb-1 text-xs text-n-slate-10"> Nome </label>

                <input
                  v-model="plan.name"
                  type="text"
                  placeholder="600 Mega"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                />
              </div>

              <div>
                <label class="block mb-1 text-xs text-n-slate-10">
                  Preço
                </label>

                <input
                  v-model="plan.price"
                  type="text"
                  placeholder="R$ 110,00/mês"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                />
              </div>

              <div>
                <label class="block mb-1 text-xs text-n-slate-10">
                  Benefícios
                </label>

                <input
                  v-model="plan.details"
                  type="text"
                  placeholder="GloboPlay por 12 meses"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                />
              </div>
            </div>

            <div class="mt-3">
              <label class="block mb-1 text-xs text-n-slate-10">
                Formas de reconhecer o plano
              </label>

              <textarea
                :value="planAliasesText(plan)"
                rows="2"
                placeholder="600&#10;600 mega"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                @change="setPlanAliases(plan, $event.target.value)"
              />
            </div>

            <div class="flex justify-end mt-3">
              <button
                type="button"
                class="px-3 py-2 text-sm border rounded-lg border-n-ruby-7 text-n-ruby-11"
                @click="removePlan(city, planIndex)"
              >
                Excluir plano
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-7">
      <h4 class="font-semibold text-n-slate-12">Mensagens do fluxo</h4>

      <div class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
        <div>
          <label class="block mb-1 text-sm text-n-slate-12">
            Pergunta da cidade
          </label>
          <textarea
            v-model="flow.messages.ask_city"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm text-n-slate-12">
            Pergunta do endereço
          </label>
          <textarea
            v-model="flow.messages.ask_address"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm text-n-slate-12">
            Pergunta do CEP
          </label>
          <textarea
            v-model="flow.messages.ask_cep"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm text-n-slate-12">
            Pergunta do bairro
          </label>
          <textarea
            v-model="flow.messages.ask_district"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm text-n-slate-12">
            Solicitação da localização
          </label>
          <textarea
            v-model="flow.messages.ask_location"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm text-n-slate-12">
            Localização recebida
          </label>
          <textarea
            v-model="flow.messages.location_received"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          />
        </div>
      </div>
    </div>
  </div>
</template>
